import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import usePersistentState from '../../use-persistent-state';
import { MetricEnum, WindowEnum } from '../../volatility-cones-provider';
import { WebSocketContext } from '../../websocket-context/websocket-provider';

interface ImpliedRealizedVolatilityProviderProps {
  children: ReactNode;
  providerKey?: string;
}

interface ImpliedRealizedVolatilityContextType {
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  data: { data: any; asAtTime: string } | null;
  setData: (data: any) => void;
  error: boolean;
  time: string;
  setTime: (time: string) => void;
  tenor: string;
  setTenor: (period: string) => void;
  metric: string;
  setMetric: (tenor: string) => void;
}

interface IFetchImpliedOrRealizedVols {
  tenor: string;
  metric: string;
}

export const ImpliedRealizedVolatilityContext = createContext(
  {} as ImpliedRealizedVolatilityContextType,
);

export const TENOR_OPTIONS = [
  { label: '1W', value: WindowEnum.W1 },
  { label: '2W', value: WindowEnum.W2 },
  { label: '1M', value: WindowEnum.M1 },
  { label: '3M', value: WindowEnum.M3 },
  { label: '6M', value: WindowEnum.M6 },
];

export const METRIC_OPTIONS = [
  { label: '10P', value: MetricEnum.P10 },
  { label: '25P', value: MetricEnum.P25 },
  {
    label: 'ATMF',
    value: MetricEnum.ATMF,
  },
  { label: '25C', value: MetricEnum.C25 },
  { label: '10C', value: MetricEnum.C10 },
  {
    label: 'Put',
    value: 'put-group',
    items: [
      { label: '5P', value: MetricEnum.P5 },

      { label: '15P', value: MetricEnum.P15 },
      { label: '20P', value: MetricEnum.P20 },

      { label: '30P', value: MetricEnum.P30 },
      { label: '35P', value: MetricEnum.P35 },
      { label: '40P', value: MetricEnum.P40 },
    ],
  },
  {
    label: 'Call',
    value: 'call-group',
    items: [
      { label: '5C', value: MetricEnum.C5 },

      { label: '15C', value: MetricEnum.C15 },
      { label: '20C', value: MetricEnum.C20 },

      { label: '30C', value: MetricEnum.C30 },
      { label: '35C', value: MetricEnum.C35 },
      { label: '40C', value: MetricEnum.C40 },
    ],
  },
];

export const ImpliedRealizedVolatilityProvider = ({
  children,
  providerKey,
}: ImpliedRealizedVolatilityProviderProps) => {
  const STATE_PATH = ['IMPLIED_REALIZED_VOLATILITY_PROVIDER', `$${providerKey || 'DEFAULT'}`];

  const IMPLIED_REALIZED_VOL = `IMPLIED_REALIZED_VOL_ID_${providerKey}`;

  const { sendMessage, subscribeToChannel } = useContext(WebSocketContext);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState({ data: [], asAtTime: '' });
  const [error, setError] = useState(false);
  const [time, setTime] = usePersistentState(STATE_PATH, 'time', 'Live');
  const [tenor, setTenor] = usePersistentState(STATE_PATH, 'tenor', '1W');
  const [metric, setMetric] = usePersistentState(STATE_PATH, 'tenor', 'ATMF');

  const fetchImpliedOrRealizedVols = ({ tenor, metric }: IFetchImpliedOrRealizedVols) => {
    setError(false);
    setIsLoading(true);

    const metricParam = Array.isArray(metric) ? metric[0] : metric;

    const req = {
      jsonrpc: '2.0',
      id: IMPLIED_REALIZED_VOL,
      method: 'ivol_vs_rvol',
      params: {
        tenor,
        metric: metricParam,
        format: 'json',
        as_at_time: new Date().toISOString(),
      },
    };

    sendMessage(JSON.stringify(req));
    subscribeToChannel(IMPLIED_REALIZED_VOL, (response: any) => {
      if ((response && response?.error) || !response.result) {
        setIsLoading(false);
        setError(true);
        setData({
          data: [],
          asAtTime: '',
        });
        return;
      }

      setData({
        data: response.result,
        asAtTime: new Date().toISOString(),
      });
      setIsLoading(false);
    });
  };

  useEffect(() => {
    fetchImpliedOrRealizedVols({
      tenor,
      metric,
    });
  }, [tenor, metric]);

  return (
    <ImpliedRealizedVolatilityContext.Provider
      value={{
        isLoading,
        setIsLoading,
        data,
        setData,
        error,
        time,
        setTime,
        tenor,
        setTenor,
        metric,
        setMetric,
      }}
    >
      {children}
    </ImpliedRealizedVolatilityContext.Provider>
  );
};

export const useImpliedRealizedVolatility = () => useContext(ImpliedRealizedVolatilityContext);
